<template>
  <!-- Outer fills width; inner content constrained to sensible max widths -->
  <div class="w-full max-w-7xl mx-auto p-4 sm:p-6">
    <div class="p-6 bg-white rounded-md shadow-md w-full max-w-5xl">
      <h2 class="text-2xl font-semibold mb-6">Generate Native Display Report</h2>

      <form @submit.prevent="generateReport" class="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <!-- Campaign -->
        <div>
          <label class="block font-medium mb-1">Campaign</label>
          <DropdownComponent
            :items="campaigns"
            display="name"
            :selected="selectedCampaign ? selectedCampaign.name : 'Select a campaign'"
            @user_select="onCampaignSelect"
            class="w-full"
          />
        </div>

        <!-- Date Range -->
        <div>
          <label class="block font-medium mb-1">Date Range</label>
          <div class="flex gap-2">
            <input
              v-model="startDate"
              type="date"
              class="date-picker w-full"
              :min="selectedCampaign ? selectedCampaign.start_date : null"
              :max="selectedCampaign ? selectedCampaign.end_date : null"
            />
            <input
              v-model="endDate"
              type="date"
              class="date-picker w-full"
              :min="selectedCampaign ? selectedCampaign.start_date : null"
              :max="selectedCampaign ? selectedCampaign.end_date : null"
            />
          </div>
        </div>

        <!-- Granularity -->
        <div>
          <label class="block font-medium mb-1">Granularity</label>
          <DropdownComponent
            :items="['Daily','Hourly','TenMinute','Minute']"
            :selected="grouping"
            @user_select="grouping = $event"
            class="w-full"
          />
        </div>

        <!-- Country Filter (UUIDs) -->
        <div>
          <label class="block font-medium mb-1">Countries</label>
          <DropdownComponent
            :items="countries"
            display="name"
            :selected="'Add country'"
            @user_select="addCountry"
            class="w-full"
          />
          <div class="flex flex-wrap gap-2 mt-2">
            <span
              v-for="c in selectedCountries"
              :key="c.uuid"
              class="bg-blue-100 rounded-full px-3 py-1 text-sm flex items-center"
            >
              {{ c.name }}
              <button type="button" @click="removeCountry(c)" class="ml-2 text-red-500">&times;</button>
            </span>
          </div>
        </div>

        <!-- Run Filter (UUIDs) -->
        <div>
          <label class="block font-medium mb-1">Runs</label>
          <DropdownComponent
            :items="runs"
            display="name"
            :selected="'Add run'"
            @user_select="addRun"
            class="w-full"
          />
          <div class="flex flex-wrap gap-2 mt-2">
            <span
              v-for="r in selectedRuns"
              :key="r.uuid"
              class="bg-green-100 rounded-full px-3 py-1 text-sm flex items-center"
            >
              {{ r.name }}
              <button type="button" @click="removeRun(r)" class="ml-2 text-red-500">&times;</button>
            </span>
          </div>
        </div>

        <!-- Game Filter (UUIDs from /campaign-games/{uuid}) -->
        <div>
          <label class="block font-medium mb-1">Games</label>
          <DropdownComponent
            :items="availableGames"
            display="friendly_name"
            :selected="selectedCampaign ? 'Add game' : 'Select a campaign first'"
            :disabled="!selectedCampaign || availableGames.length === 0"
            @user_select="addGame"
            class="w-full"
          />
          <div class="flex flex-wrap gap-2 mt-2">
            <span
              v-for="g in selectedGames"
              :key="g.uuid"
              class="bg-indigo-100 rounded-full px-3 py-1 text-sm flex items-center"
            >
              {{ g.friendly_name }}
              <button type="button" @click="removeGame(g)" class="ml-2 text-red-500">&times;</button>
            </span>
          </div>
        </div>

        <!-- Submit -->
        <button
          type="submit"
          class="btn-primary bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          :disabled="loading"
        >
          {{ loading ? 'Queuing…' : 'Generate Report' }}
        </button>

        <div v-if="message" class="sm:col-span-2 text-center mt-2 text-green-600">
          {{ message }}
        </div>
      </form>
    </div>

    <!-- List fills the wider container; inner cells wrap so no horizontal blowout -->
    <div class="mt-8">
      <NativeDisplayReportsList v-if="!loading" :campaign-index="campaignIndex" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import NativeDisplayReportsList from '@/components/NativeDisplayReportsList.vue'

const normalizeGames = (list = []) =>
  [...new Map(
    (list || [])
      .filter(g => g && g.uuid)
      .map(g => [g.uuid, {
        uuid: g.uuid,
        name: g.name || g.friendly_name || 'Unknown',
        friendly_name: g.friendly_name || g.name || 'Unknown',
      }])
  ).values()].sort((a, b) => a.friendly_name.localeCompare(b.friendly_name))

const uniqueByUuid = arr => {
  const seen = new Set()
  return (arr || []).filter(x => x?.uuid && !seen.has(x.uuid) && seen.add(x.uuid))
}

export default {
  components: { NativeDisplayReportsList },

  data() {
    return {
      campaigns: [],
      countries: [],
      runs: [],
      games: [],

      selectedCampaign: null,
      startDate: null,
      endDate: null,
      grouping: 'Daily',

      selectedCountries: [],
      includeCountries: true,
      selectedRuns: [],
      includeRuns: true,
      selectedGames: [],
      includeGames: true,

      loading: false,
      message: '',
    }
  },

  async created() {
    const [campRes, countryRes] = await Promise.all([
      axios.get('/campaigns/get/all', {
        params: {
          un_archived_only: true
        }
      }),
      axios.get('/countries'),
    ])
    this.campaigns = campRes.data || []
    console.log('Loaded campaigns:', this.campaigns)
    this.countries = (countryRes.data || [])
      .filter(c => c?.uuid)
      .sort((a, b) => a.name.localeCompare(b.name))
    this.runs = []
    this.games = []
  },

  computed: {
    availableGames() { return this.games },

    // Map of campaign UUID -> name for table rendering
    campaignIndex() {
      return (this.campaigns || []).reduce((acc, c) => {
        if (!c?.uuid) return acc
        acc[c.uuid] = c.name || c.uuid
        return acc
      }, {})
    },

    // UUID payloads for backend (pivot-based)
    payloadFilters() {
      return {
        countries: uniqueByUuid(this.selectedCountries).map(c => c.uuid),
        runs:      uniqueByUuid(this.selectedRuns).map(r => r.uuid),
        games:     uniqueByUuid(this.selectedGames).map(g => g.uuid),
      }
    },
  },

  watch: {
    async selectedCampaign(c) {
      this.runs = []
      this.games = []
      this.selectedRuns = []
      this.selectedGames = []

      if (!c) return

      this.startDate = c.start_date
      this.endDate   = c.end_date

      // Runs for selected campaign
      axios.get(`/runs/get-campaign-runs/${c.uuid}`)
        .then(r => {
          this.runs = (r.data || [])
            .filter(x => x?.uuid)
            .sort((a, b) => a.name.localeCompare(b.name))
        })
        .catch(() => { this.runs = [] })

      // Games ONLY from campaign-games endpoint
      try {
        const { data } = await axios.get(`/campaign-games/${c.uuid}`)
        this.games = normalizeGames(Array.isArray(data) ? data : (data?.data || []))
      } catch (e) {
        console.error(e)
        this.games = []
      }
    }
  },

  methods: {
    onCampaignSelect(campaign) {
      this.selectedCampaign = campaign
    },

    addCountry(country) {
      if (!country?.uuid) return
      if (this.selectedCountries.some(x => x.uuid === country.uuid)) return
      this.selectedCountries = uniqueByUuid([...this.selectedCountries, country])
      this.countries = this.countries.filter(x => x.uuid !== country.uuid)
    },
    removeCountry(country) {
      if (!country?.uuid) return
      this.selectedCountries = this.selectedCountries.filter(x => x.uuid !== country.uuid)
      this.countries = uniqueByUuid([...this.countries, country])
        .sort((a, b) => a.name.localeCompare(b.name))
    },

    addRun(run) {
      if (!run?.uuid) return
      if (this.selectedRuns.some(x => x.uuid === run.uuid)) return
      this.selectedRuns = uniqueByUuid([...this.selectedRuns, run])
      this.runs = this.runs.filter(x => x.uuid !== run.uuid)
    },
    removeRun(run) {
      if (!run?.uuid) return
      this.selectedRuns = this.selectedRuns.filter(x => x.uuid !== run.uuid)
      this.runs = uniqueByUuid([...this.runs, run]).sort((a, b) => a.name.localeCompare(b.name))
    },

    addGame(game) {
      if (!game?.uuid) return
      if (this.selectedGames.some(x => x.uuid === game.uuid)) return
      this.selectedGames = uniqueByUuid([...this.selectedGames, game])
      this.games = this.games.filter(x => x.uuid !== game.uuid)
    },
    removeGame(game) {
      if (!game?.uuid) return
      this.selectedGames = this.selectedGames.filter(x => x.uuid !== game.uuid)
      this.games = normalizeGames([...this.games, game])
    },

    async generateReport() {
      if (!this.selectedCampaign) {
        this.message = 'Please select a campaign'
        return
      }

      this.loading = true
      this.message = ''

      try {
        const { countries, runs, games } = this.payloadFilters

        await axios.post('/native-display-reports', {
          campaign_uuid:       this.selectedCampaign.uuid,
          start_date:          this.startDate,
          end_date:            this.endDate,
          impression_grouping: this.grouping,

          country_filter:      countries,          // array of UUID strings
          include_countries:   this.includeCountries,

          run_filter:          runs,               // array of UUID strings
          include_runs:        this.includeRuns,

          game_filter:         games,              // array of UUID strings
          include_games:       this.includeGames,
        })

        this.message = 'Report queued successfully!'
      } catch (e) {
        console.error(e)
        this.message = 'Error queuing report'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.date-picker { @apply border rounded p-2 w-full; }
</style>